import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiGetJson, apiPost } from "../../utils/apiClient";
import { useUserContext } from "../../contexts/UserContext";

type Level = "info" | "warning" | "critical";
type TargetType = "all" | "company" | "shop";

type CompanyLite = {
    tenantId?: string;
};

type MeResponse = {
    ok?: boolean;
    superAdmin?: boolean;
    superAdminGlobal?: boolean;
    superAdminTenants?: string[];
};

const OEM_TENANT_ID = "ecvision";

export default function SuperNotificationNewPage() {
    const navigate = useNavigate();
    const user = useUserContext();

    const myTenantId = String((user as any)?.tenantId ?? "").trim();
    const isEcvision = myTenantId === OEM_TENANT_ID;
    const isSuperAdmin = (user as any)?.superAdmin === true;
    const isGlobalSuperUser = (user as any)?.superAdminGlobal === true;
    const [meSuperAdmin, setMeSuperAdmin] = useState(false);
    const [meSuperAdminGlobal, setMeSuperAdminGlobal] = useState(false);
    const [meSuperAdminTenants, setMeSuperAdminTenants] = useState<string[]>([]);
    const superAdminTenants: string[] = Array.isArray((user as any)?.superAdminTenants)
        ? (user as any).superAdminTenants.map((x: any) => String(x ?? "").trim()).filter(Boolean)
        : [];
    const mergedSuperAdmin = isSuperAdmin || meSuperAdmin;
    const mergedSuperAdminGlobal = isGlobalSuperUser || meSuperAdminGlobal;
    const mergedSuperAdminTenants = Array.from(new Set([...superAdminTenants, ...meSuperAdminTenants]));
    const scopedTenantIds = mergedSuperAdminGlobal
        ? []
        : (mergedSuperAdminTenants.length > 0
            ? mergedSuperAdminTenants
            : (myTenantId ? [myTenantId] : []));
    const hasEcvisionAccess =
        isEcvision ||
        mergedSuperAdminGlobal ||
        mergedSuperAdminTenants.includes(OEM_TENANT_ID);
    const canChooseTenant =
        mergedSuperAdmin ||
        mergedSuperAdminGlobal ||
        isEcvision ||
        mergedSuperAdminTenants.length > 0;

    const [title, setTitle] = useState("");
    const [body, setBody] = useState("");
    const [level, setLevel] = useState<Level>("info");

    // ★ ecvisionのみ：全体表示フラグ
    const [isGlobal, setIsGlobal] = useState(false);

    const [targetType, setTargetType] = useState<TargetType>("all");
    const [targetIdsRaw, setTargetIdsRaw] = useState("");
    const [docTenantId, setDocTenantId] = useState(myTenantId || OEM_TENANT_ID);
    const [tenantOptions, setTenantOptions] = useState<string[]>([]);

    const [isActive, setIsActive] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const canSetGlobalNotice = hasEcvisionAccess && docTenantId === OEM_TENANT_ID;

    useEffect(() => {
        let cancelled = false;

        (async () => {
            try {
                const me = await apiGetJson<MeResponse>("/api/me");
                if (cancelled) return;

                const s = me?.superAdmin === true;
                const g = me?.superAdminGlobal === true;
                const ts = Array.isArray(me?.superAdminTenants)
                    ? me!.superAdminTenants!.map((x) => String(x ?? "").trim()).filter(Boolean)
                    : [];

                setMeSuperAdmin(s);
                setMeSuperAdminGlobal(g);
                setMeSuperAdminTenants(ts);

                console.log("[SuperNotificationNewPage] /api/me super", {
                    superAdmin: s,
                    superAdminGlobal: g,
                    superAdminTenants: ts,
                });
            } catch (e: any) {
                if (cancelled) return;
                console.warn("[SuperNotificationNewPage] /api/me failed", e?.message ?? e);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        // ユーザー操作後に作成テナントを勝手に上書きしない。
        // 初期値が空のときだけ、ログインテナント(or ecvision)で補完する。
        if (String(docTenantId ?? "").trim()) return;
        setDocTenantId(myTenantId || OEM_TENANT_ID);
    }, [myTenantId, docTenantId]);

    useEffect(() => {
        if (docTenantId !== OEM_TENANT_ID && isGlobal) {
            setIsGlobal(false);
        }
    }, [docTenantId, isGlobal]);

    useEffect(() => {
        if (!tenantOptions.length) return;
        if (tenantOptions.includes(docTenantId)) return;
        if (tenantOptions.includes(OEM_TENANT_ID)) {
            setDocTenantId(OEM_TENANT_ID);
            return;
        }
        setDocTenantId(tenantOptions[0]);
    }, [tenantOptions, docTenantId]);

    useEffect(() => {
        let cancelled = false;

        (async () => {
            try {
                const rows = await apiGetJson<CompanyLite[]>("/api/super/companies");
                if (cancelled) return;

                const set = new Set<string>();
                if (mergedSuperAdminGlobal) {
                    if (myTenantId) set.add(myTenantId);
                    for (const row of rows ?? []) {
                        const t = String(row?.tenantId ?? "").trim();
                        if (t) set.add(t);
                    }
                } else {
                    for (const t of scopedTenantIds) {
                        if (t) set.add(t);
                    }
                    if (hasEcvisionAccess) set.add(OEM_TENANT_ID);
                }

                setTenantOptions(Array.from(set).sort((a, b) => a.localeCompare(b, "ja")));
            } catch {
                if (cancelled) return;
                const base = mergedSuperAdminGlobal
                    ? [myTenantId, OEM_TENANT_ID].filter(Boolean)
                    : scopedTenantIds;
                setTenantOptions(Array.from(new Set(base)));
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [canChooseTenant, hasEcvisionAccess, mergedSuperAdminGlobal, myTenantId, scopedTenantIds]);

    const submit = async () => {
        setError(null);

        if (!title.trim()) {
            setError("タイトルは必須です");
            return;
        }

        const effectiveTenantId = String(docTenantId ?? "").trim() || String(myTenantId ?? "").trim();

        if (!effectiveTenantId) {
            setError("作成先テナントを指定してください");
            return;
        }

        // 全体通知は all に寄せる（事故防止）
        const effectiveTargetType: TargetType = canSetGlobalNotice && isGlobal ? "all" : targetType;

        const targetIds =
            effectiveTargetType === "all"
                ? []
                : targetIdsRaw
                    .split("\n")
                    .map((s) => s.trim())
                    .filter(Boolean);

        try {
            setSaving(true);

            const payload: any = {
                title,
                body,
                level,
                targetType: effectiveTargetType,
                targetIds,
                isActive,
            };

            payload.isGlobal = canSetGlobalNotice ? isGlobal : false;
            payload.tenantId = effectiveTenantId;

            await apiPost("/api/super/notifications", payload);

            navigate("/super/notifications");
        } catch (e: any) {
            setError("保存に失敗しました");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="max-w-3xl space-y-6">
            <h1 className="text-lg font-semibold">お知らせ 新規作成</h1>

            <div className="text-[11px] text-gray-500 border rounded px-2 py-1 bg-gray-50">
                debug: tenant={myTenantId || "-"} / canChooseTenant={String(canChooseTenant)} / hasEcvisionAccess={String(hasEcvisionAccess)} / super={String(mergedSuperAdmin)} / global={String(mergedSuperAdminGlobal)} / tenants={mergedSuperAdminTenants.join(",") || "-"} / docTenant={docTenantId || "-"} / canSetGlobalNotice={String(canSetGlobalNotice)}
            </div>

            {error && (
                <div className="text-sm text-red-600 border border-red-300 bg-red-50 px-3 py-2 rounded">
                    {error}
                </div>
            )}

            <div className="space-y-1">
                <label className="text-sm font-medium">作成テナント</label>
                <select
                    className="border rounded px-3 py-2 text-sm min-w-[260px]"
                    value={docTenantId}
                    onChange={(e) => setDocTenantId(e.target.value)}
                >
                    {(tenantOptions.length > 0 ? tenantOptions : [myTenantId || OEM_TENANT_ID]).map((t) => (
                        <option key={t} value={t}>
                            {t}
                        </option>
                    ))}
                </select>
            </div>

            {/* ecvisionのみ：全体表示 */}
            {canSetGlobalNotice && (
                <label className="flex items-center gap-2">
                    <input
                        type="checkbox"
                        checked={isGlobal}
                        onChange={(e) => setIsGlobal(e.target.checked)}
                    />
                    <span className="text-sm">全テナントに表示（ecvision 全体通知）</span>
                </label>
            )}

            {canSetGlobalNotice && (
                <div className="text-[11px] text-gray-600">
                    ※ONにすると ecvision / ecx を含む全テナントに表示されます。管理上、この通知は ecvision テナントの全体通知として保存されます。
                </div>
            )}

            {/* タイトル */}
            <div className="space-y-1">
                <label className="text-sm font-medium">タイトル *</label>
                <input
                    className="w-full border rounded px-3 py-2 text-sm"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                />
            </div>

            {/* 本文 */}
            <div className="space-y-1">
                <label className="text-sm font-medium">本文</label>
                <textarea
                    className="w-full border rounded px-3 py-2 text-sm min-h-[120px]"
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                />
                ※1行表示の短文はテキスト可／改行・複数行はHTML必須
            </div>

            {/* 重要度 */}
            <div className="space-y-1">
                <label className="text-sm font-medium">重要度</label>
                <select
                    className="border rounded px-3 py-2 text-sm"
                    value={level}
                    onChange={(e) => setLevel(e.target.value as Level)}
                >
                    <option value="info">info（情報）</option>
                    <option value="warning">warning（注意）</option>
                    <option value="critical">critical（重要）</option>
                </select>
            </div>

            {/* 対象 */}
            <div className="space-y-1">
                <label className="text-sm font-medium">対象</label>
                <select
                    className="border rounded px-3 py-2 text-sm"
                    value={isGlobal ? "all" : targetType}
                    disabled={isGlobal} // 全体通知は all 固定
                    onChange={(e) => setTargetType(e.target.value as TargetType)}
                >
                    <option value="all">全ユーザー</option>
                    {/*<option value="company">特定会社</option>*/}
                    <option value="shop">特定ショップ</option>
                </select>
                {isGlobal && (
                    <div className="text-[11px] text-gray-600">
                        全体通知は “全ユーザー” 固定です（対象ショップ指定は不可）
                    </div>
                )}
            </div>

            {/* 対象ID */}
            {!isGlobal && targetType !== "all" && (
                <div className="space-y-1">
                    <label className="text-sm font-medium">対象ID（1行1つ）</label>
                    <textarea
                        className="w-full border rounded px-3 py-2 text-sm min-h-[80px]"
                        value={targetIdsRaw}
                        onChange={(e) => setTargetIdsRaw(e.target.value)}
                        placeholder={targetType === "company" ? "companyId を1行ずつ入力" : "shopUrl を1行ずつ入力"}
                    />
                </div>
            )}

            {/* 有効 */}
            <div className="flex items-center gap-2">
                <input
                    type="checkbox"
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                />
                <span className="text-sm">有効にする</span>
            </div>

            {/* 操作 */}
            <div className="flex gap-3">
                <button
                    onClick={submit}
                    disabled={saving}
                    className="px-4 py-2 rounded bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-50"
                >
                    {saving ? "保存中…" : "保存"}
                </button>

                <button
                    onClick={() => navigate("/super/notifications")}
                    className="px-4 py-2 rounded border text-sm"
                >
                    キャンセル
                </button>
            </div>
        </div>
    );
}
